import { api } from './client';
import { downloadBlob } from './iaaExports';

/** PMS securities master (read). Open to any authenticated user; write APIs +
 *  upload feeds are later increments.
 *  Design: decisions/2026-07-20_securities-master-attributes-design.md. */

export interface SecurityPrice {
  quote_date: string;
  price: number | null;
  variation: number | null;
  quote_source: string | null;
  quote_currency: string | null;
}

export interface SecurityClassificationReading {
  dimension_code: string;
  dimension_name: string;
  value_id: string;
  value_label: string;
  tier_level: number;
  path: string[];
}

export interface SecurityRow {
  id: string;
  isin: string;
  currency: string;
  bb_ticker: string | null;
  ticker: string | null;
  lei: string | null;
  figi: string | null;
  valoren: string | null;
  source_asset_id: string | null;
  description: string;
  fund_type: string | null;
  is_benchmark: boolean;
  /** "Approved" (TRADE-1): may be bought / kept in a trade. Every security starts not approved. */
  authorized: boolean;
  associated_benchmark_security_id: string | null;
  associated_benchmark_label: string | null;
  benchmark_name: string | null;
  benchmark_bb_ticker: string | null;
  issuer: string | null;
  issuer_industry_text: string | null;
  coupon_rate: number | null;
  issue_date: string | null;
  first_coupon_date: string | null;
  maturity_date: string | null;
  call_date: string | null;
  rating: string | null;
  rating_agency: string | null;
  rating_date: string | null;
  esma_risk: string | null;
  sfdr: string | null;
  mifid_complex: boolean | null;
  mifid_complex_attribute: string | null;
  mifid_firds_indicator: string | null;
  mifid_admitted_to_trading: boolean | null;
  primary_exchange_mic: string | null;
  trading_venue_mic: string | null;
  management_fee: number | null;
  ter: number | null;
  min_piece: number | null;
  min_increment: number | null;
  comments: string | null;
  is_deleted: boolean;
  asset_class: string | null;
  latest_price: number | null;
  latest_price_date: string | null;
  classifications: SecurityClassificationReading[];
}

export interface SecurityList {
  items: SecurityRow[];
  total: number;
  limit: number;
  offset: number;
}

export type SecurityDetail = SecurityRow & { prices: SecurityPrice[] };

/** Create body (Inc 1). Identity isin/currency/description required + set once
 *  (frozen — C-294); the rest optional. */
export interface SecurityCreatePayload {
  isin: string;
  currency: string;
  description: string;
  bb_ticker?: string | null;
  ticker?: string | null;
  lei?: string | null;
  figi?: string | null;
  valoren?: string | null;
  source_asset_id?: string | null;
  fund_type?: string | null;
  is_benchmark?: boolean;
  authorized?: boolean;
  benchmark_name?: string | null;
  benchmark_bb_ticker?: string | null;
  issuer?: string | null;
  issuer_industry_text?: string | null;
  coupon_rate?: number | null;
  issue_date?: string | null;
  first_coupon_date?: string | null;
  maturity_date?: string | null;
  call_date?: string | null;
  rating?: string | null;
  rating_agency?: string | null;
  rating_date?: string | null;
  esma_risk?: string | null;
  sfdr?: string | null;
  mifid_complex?: boolean | null;
  mifid_complex_attribute?: string | null;
  mifid_firds_indicator?: string | null;
  mifid_admitted_to_trading?: boolean | null;
  primary_exchange_mic?: string | null;
  trading_venue_mic?: string | null;
  management_fee?: number | null;
  ter?: number | null;
  min_piece?: number | null;
  min_increment?: number | null;
  comments?: string | null;
}

/** Update body: editable attributes only. The frozen identity fields
 *  (isin, currency, bb_ticker, figi) are NOT updatable in-app (C-294). */
export type SecurityUpdatePayload = Partial<
  Omit<SecurityCreatePayload, 'isin' | 'currency' | 'bb_ticker' | 'figi'>
>;

// ── attributes upload (Inc 3b-i) ──────────────────────────────────────────────
export type SecuritiesImportMode = 'preview' | 'commit';
export type SecuritiesImportConflict = 'skip' | 'overwrite' | 'replace';

export interface SecuritiesImportReport {
  mode: string;
  on_conflict: string;
  total_rows: number;
  new_count: number;
  matched_count: number;
  changed_count: number;
  field_change_count: number;
  delete_count: number;
  new_sample: string[];
  changed_sample: string[];
  delete_sample: string[];
  errors: string[];
  blocking_errors: string[];
  /** Model Portfolios M1 (MQ5): securities this upload would un-approve that a bucket or a
   *  portfolio holds — a warning; the ids feed the downloadable list. */
  unapproved_in_use: string[];
  unapproved_in_use_ids: string[];
  inserted: number;
  updated: number;
  skipped: number;
  deleted: number;
}

export interface SecurityListParams {
  q?: string;
  is_benchmark?: boolean;
  classificationValueIds?: string[];
  limit?: number;
  offset?: number;
}

function toQuery(p: SecurityListParams): string {
  const sp = new URLSearchParams();
  if (p.q) sp.set('q', p.q);
  if (p.is_benchmark !== undefined) sp.set('is_benchmark', String(p.is_benchmark));
  (p.classificationValueIds ?? []).forEach((v) => sp.append('classification_value_id', v));
  if (p.limit != null) sp.set('limit', String(p.limit));
  if (p.offset != null) sp.set('offset', String(p.offset));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const securitiesApi = {
  list: (p: SecurityListParams = {}) => api.get<SecurityList>(`/securities${toQuery(p)}`),
  get: (id: string) => api.get<SecurityDetail>(`/securities/${encodeURIComponent(id)}`),
  /** Export the on-screen (filtered + sorted) rows as .xlsx, always the FULL column
   *  set. `ids` = displayed row ids in display order; [] exports every security.
   *  Streams a blob → browser save. */
  exportXlsx: async (ids: string[] = []): Promise<void> => {
    const res = await fetch('/securities/export', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error('The securities export could not be generated.');
    downloadBlob(await res.blob(), 'securities-master.xlsx');
  },
  /** Write side (Inc 1) — all gated `securities.manage`. */
  create: (body: SecurityCreatePayload) => api.post<SecurityRow>('/securities', body),
  update: (id: string, body: SecurityUpdatePayload) =>
    api.patch<SecurityRow>(`/securities/${encodeURIComponent(id)}`, body),
  remove: (id: string) => api.delete<SecurityRow>(`/securities/${encodeURIComponent(id)}`),
  /** Inline classification editing (Inc 2): dim code → value id (assign/reclassify)
   *  or null (clear). One value per dimension. */
  setClassifications: (id: string, assignments: Record<string, string | null>) =>
    api.put<SecurityRow>(`/securities/${encodeURIComponent(id)}/classifications`, { assignments }),

  /** Bulk attributes upload (Inc 3b-i). preview parses + reconciles and writes
   *  nothing; commit applies. The importer is stateless, so keep the same File and
   *  re-send it on commit. Multipart, so a raw fetch (not the JSON client). */
  async importFile(
    file: File, mode: SecuritiesImportMode, onConflict: SecuritiesImportConflict,
  ): Promise<SecuritiesImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('on_conflict', onConflict);
    const res = await fetch('/securities/import', { method: 'POST', credentials: 'include', body: form });
    if (!res.ok) {
      const j = await res.json().catch(() => null);
      throw new Error((j && j.detail) || 'The securities file could not be processed.');
    }
    return res.json();
  },
  /** Download the blank attributes upload template (.xlsx). */
  async downloadTemplate(): Promise<void> {
    const res = await fetch('/securities/import-template', { method: 'GET', credentials: 'include' });
    if (!res.ok) throw new Error('The template could not be generated.');
    downloadBlob(await res.blob(), 'securities-template.xlsx');
  },
};
